import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess, AppFeatureService, ApplicationApiClientAccess } from '@nk/access-block';
import { CachingService } from '@nk/caching-lib';
import {
  AppFeatureEntity,
  AppFeatureKey,
  COMPANY_FLAG_FEATURES,
  COMPANY_FLAG_ROLES,
  CompanyFlagCompositeEntity,
  CompanyFlagDashboardCompanyComposite,
  CompanyFlagHistoryCompositeEntity,
  CompanyFlagHistoryEntity,
  CompanyFlagTypeAggregateComposite,
  CompanyFlagTypeEntity,
  CompanyStateCategoryEntity,
  CompanyStateEntity,
  COUNTRY_FEATURES,
  SimpleCollection,
  WatchlistCompanyFlagAggregateEntity,
} from '@nk/data-entities-lib';
import { arrayToDict, arrayToPickyDict, Dict, DictList } from '@nk/utils-lib';
import { combineLatest, forkJoin, from, Observable, of } from 'rxjs';
import { concatMap, first, map, mergeMap } from 'rxjs/operators';

export interface CompanyFlagTypeAppFeatureCompositeEntity {
  companyFlagType: CompanyFlagTypeEntity;
  appFeature: AppFeatureEntity;
}

@Injectable()
export class CompanyFlagService {
  constructor(
    private _applicationApiAccess: ApplicationApiClientAccess,
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _cachingService: CachingService,
    private _appFeatureService: AppFeatureService,
  ) {}

  public companyFlagCompositeByCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyFlagCompositeByCompanyUuid,
  );
  public fetchCompanyFlagCompositeByCompanyUuid(
    companyUuid: string,
    lookbackDays?: number,
    lookbackDate?: string,
    noContextFlag: boolean = null,
    companyFlagTypeIdentifiers?: string[],
  ): Observable<CompanyFlagCompositeEntity[]> {
    let httpParams = new HttpParams();
    if (lookbackDays) {
      httpParams = httpParams.append('lookback_days', String(lookbackDays));
    }
    if (lookbackDate) {
      httpParams = httpParams.append('lookback_date', String(lookbackDate));
    }
    if (companyFlagTypeIdentifiers && companyFlagTypeIdentifiers.length) {
      httpParams = httpParams.append('company_flag_type_identifiers', companyFlagTypeIdentifiers.join(','));
    }
    if (noContextFlag !== null) {
      httpParams = httpParams.append('no_context_flag', String(noContextFlag));
    }
    return this._analysisApiAccess.get<CompanyFlagCompositeEntity[]>(
      `company/${companyUuid}/company-flag-composite`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.collateral, ...COMPANY_FLAG_FEATURES, ...COUNTRY_FEATURES],
      [...COMPANY_FLAG_ROLES],
    );
  }

  public companyFlagHistoryByCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyFlagHistoryByCompanyUuid,
  );
  public fetchCompanyFlagHistoryByCompanyUuid(companyUuid: string): Observable<CompanyFlagHistoryEntity[]> {
    return this._analysisApiAccess.get<CompanyFlagHistoryEntity[]>(
      `company/${companyUuid}/company-flag-history`,
      {},
      [AppFeatureKey.base, AppFeatureKey.collateral, ...COMPANY_FLAG_FEATURES, ...COUNTRY_FEATURES],
      [...COMPANY_FLAG_ROLES],
    );
  }

  public companyFlagHistoryCompositeByCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyFlagHistoryCompositeByCompanyUuid,
  );
  public fetchCompanyFlagHistoryCompositeByCompanyUuid(
    companyUuid: string,
  ): Observable<CompanyFlagHistoryCompositeEntity[]> {
    return this._analysisApiAccess.get<CompanyFlagHistoryCompositeEntity[]>(
      `company/${companyUuid}/company-flag-history-composite`,
      {},
      [AppFeatureKey.base, AppFeatureKey.collateral, ...COMPANY_FLAG_FEATURES, ...COUNTRY_FEATURES],
      [...COMPANY_FLAG_ROLES],
    );
  }

  public companyFlagDashboardCompanyCompositeCollectionCache = this._cachingService.create(
    this,
    this.fetchCompanyFlagDashboardCompanyCompositeCollection,
  );
  public fetchCompanyFlagDashboardCompanyCompositeCollection(
    watchlistUuids: string[] = [],
    companyFlagCategoryKeys: string[] = [],
    companyFlagTypeUuids: string[] = [],
    ignoreCompanyFlagTypeUuids: string[] = [],
    timeHorizon: 'weeks' | 'days' | 'months' = 'weeks',
    timeHorizonValue = 1,
    limit = 10,
    offset = 0,
  ): Observable<SimpleCollection<CompanyFlagDashboardCompanyComposite>> {
    let params = new HttpParams();
    if (Array.isArray(watchlistUuids) && watchlistUuids.length) {
      params = params.append('watchlist_uuids', watchlistUuids.join(','));
    }
    if (Array.isArray(ignoreCompanyFlagTypeUuids) && ignoreCompanyFlagTypeUuids.length) {
      params = params.append('company_flag_type_ignore_list', ignoreCompanyFlagTypeUuids.join(','));
    }
    if (Array.isArray(companyFlagTypeUuids) && companyFlagTypeUuids.length) {
      params = params.append('company_flag_type_uuids', companyFlagTypeUuids.join(','));
    }
    if (Array.isArray(companyFlagCategoryKeys) && companyFlagCategoryKeys.length) {
      params = params.append('company_flag_category_keys', companyFlagCategoryKeys.join(','));
    }
    params = params.append('time_horizon', timeHorizon);
    params = params.append('time_horizon_value', timeHorizonValue);
    params = params.append('limit', limit);
    params = params.append('offset', offset);
    return this._applicationApiAccess.get<SimpleCollection<CompanyFlagDashboardCompanyComposite>>(
      'company-flag-dashboard-company-composite-collection',
      { params },
      [AppFeatureKey.base, AppFeatureKey.collateral, ...COMPANY_FLAG_FEATURES],
      [...COMPANY_FLAG_ROLES],
    );
  }

  public companyFlagTypeAggregateCompositeCache = this._cachingService.create(
    this,
    this.fetchCompanyFlagTypeAggregateComposite,
  );
  public fetchCompanyFlagTypeAggregateComposite(
    watchlistUuids: string[],
    companyFlagCategoryKeys: string[],
    ignoreCompanyFlagTypeUuids: string[],
    timeHorizon: 'weeks' | 'days' | 'months' = 'weeks',
    timeHorizonValue = 1,
  ): Observable<CompanyFlagTypeAggregateComposite[]> {
    let params = new HttpParams();
    if (Array.isArray(watchlistUuids) && watchlistUuids.length) {
      params = params.append('watchlist_uuids', watchlistUuids.join(','));
    }
    if (Array.isArray(ignoreCompanyFlagTypeUuids) && ignoreCompanyFlagTypeUuids.length) {
      params = params.append('company_flag_type_ignore_list', ignoreCompanyFlagTypeUuids.join(','));
    }
    if (Array.isArray(companyFlagCategoryKeys) && companyFlagCategoryKeys.length) {
      params = params.append('company_flag_category_keys', companyFlagCategoryKeys.join(','));
    }
    params = params.append('time_horizon', timeHorizon);
    params = params.append('time_horizon_value', timeHorizonValue);
    return this._applicationApiAccess.get<CompanyFlagTypeAggregateComposite[]>(
      'company-flag-type-aggregate-composite',
      { params },
      [AppFeatureKey.base, AppFeatureKey.collateral, ...COMPANY_FLAG_FEATURES],
      [...COMPANY_FLAG_ROLES],
    );
  }

  public companyFlagTypeListCache = this._cachingService.create(this, this.fetchCompanyFlagTypeList);
  public fetchCompanyFlagTypeList(): Observable<CompanyFlagTypeEntity[]> {
    return this._analysisApiAccess.get<CompanyFlagTypeEntity[]>(
      'company-flag-type',
      {},
      [AppFeatureKey.base, AppFeatureKey.collateral, ...COMPANY_FLAG_FEATURES],
      [...COMPANY_FLAG_ROLES],
    );
  }

  public companyStateCategoryListCache = this._cachingService.create(this, this.fetchCompanyStateCategoryList);
  public fetchCompanyStateCategoryList(): Observable<CompanyStateCategoryEntity[]> {
    return this._analysisApiAccess.get<CompanyStateCategoryEntity[]>('company-state-category');
  }

  public companyStateByCompanyStateCategoryListCache = this._cachingService.create(
    this,
    this.fetchCompanyStateByCompanyStateCategoryList,
  );
  public fetchCompanyStateByCompanyStateCategoryList(
    companyStateCategoryUuid: string,
  ): Observable<CompanyStateEntity[]> {
    return this._analysisApiAccess.get<CompanyStateEntity[]>(
      `company-state-category/${companyStateCategoryUuid}/company-state`,
    );
  }

  public watchlistCompanyFlagAggregateCache = this._cachingService.create(
    this,
    this.fetchWatchlistCompanyFlagAggregate,
  );
  public fetchWatchlistCompanyFlagAggregate(watchlistUuid: string): Observable<WatchlistCompanyFlagAggregateEntity[]> {
    return this._applicationApiAccess.get<WatchlistCompanyFlagAggregateEntity[]>(
      `watchlist/${watchlistUuid}/watchlist-company-flag-aggregate`,
      {},
      [AppFeatureKey.base, AppFeatureKey.collateral, AppFeatureKey.extended_company_flags],
    );
  }

  public streamCompanyFlagHistoryCompositeFromCompanyList(
    companyUuidList: string[],
  ): Observable<DictList<CompanyFlagHistoryCompositeEntity>> {
    return from(companyUuidList).pipe(
      concatMap((companyUuid) => {
        return forkJoin([this.companyFlagHistoryCompositeByCompanyCache.get(companyUuid), of(companyUuid)]);
      }),
      mergeMap((payload) => {
        const companyFlagCompositeList = payload[0];
        const companyUuid = payload[1];
        return of({
          [companyUuid]: companyFlagCompositeList,
        });
      }),
    );
  }

  public legalCompanyFlagList(): Observable<Dict<CompanyFlagTypeEntity>> {
    return combineLatest([
      this._appFeatureService.appFeatureListByMembershipCache.get().pipe(first()),
      this.companyFlagTypeListCache.get().pipe(first()),
    ]).pipe(
      map((payload) => {
        const [legalAppFeatures, companyFlagTypes] = payload;
        const legalAppFeatureUuids = legalAppFeatures.map((feature) => feature.uuid);
        const legalCompanyFlags = companyFlagTypes.filter((flag) =>
          legalAppFeatureUuids.includes(flag.app_feature_uuid),
        );
        return arrayToDict(legalCompanyFlags, (a) => a.uuid);
      }),
    );
  }

  public companyFlagTypeAppFeatureCompositeList(): Observable<CompanyFlagTypeAppFeatureCompositeEntity[]> {
    return forkJoin([
      this._appFeatureService.appFeatureListCache.get().pipe(first()),
      this.companyFlagTypeListCache.get().pipe(first()),
    ]).pipe(
      map((payload) => {
        const [appFeatures, companyFlagTypes] = payload;
        const appFeatureDict = arrayToDict(appFeatures, (f) => f.uuid);
        return companyFlagTypes.map((cft) => {
          return {
            companyFlagType: cft,
            appFeature: appFeatureDict[cft.app_feature_uuid],
          };
        });
      }),
    );
  }

  public companyFlagTypeUuidToAppFeatureKeyDict(): Observable<Dict<string>> {
    return this.companyFlagTypeAppFeatureCompositeList().pipe(
      map((composites) => {
        return arrayToPickyDict(
          composites,
          (k) => k.companyFlagType.uuid,
          (v) => v.appFeature.app_feature_key,
        );
      }),
    );
  }
}
